import { clsx } from 'clsx';
import { ComponentPropsWithRef, useEffect, useId, useRef } from 'react';
import { inputError, inputLabel } from '../input/input.css.ts';
import { space } from '../tokens.ts';
import { useComposedRefs } from '../utils/use-composed-refs.ts';
import { textarea, textareaContainer, textareaVariants } from './textarea.css';

export interface TextareaProps extends ComponentPropsWithRef<'textarea'> {
  label?: string;
  errorMessage?: string;
  autoResize?: boolean;
  containerClassName?: string;
}

export const Textarea = function TextareaComponent({
  ref,
  className,
  errorMessage,
  label,
  id,
  autoResize,
  onChange,
  containerClassName,
  ...rest
}: TextareaProps) {
  const reactId = useId();
  const textareaId = id || reactId;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoResize && textareaContainerRef.current && textareaRef.current) {
      textareaContainerRef.current.style.height = 'auto';
      textareaContainerRef.current.style.height = `${textareaRef.current.scrollHeight + space.s2}px`;
    }
  }, [rest.value, autoResize, rest.placeholder]);

  return (
    <div>
      {label && (
        <label htmlFor={textareaId} className={inputLabel}>
          {label}
        </label>
      )}
      <div
        className={clsx(
          textareaVariants[errorMessage ? 'error' : 'default'],
          textareaContainer,
          containerClassName,
        )}
        ref={textareaContainerRef}
      >
        <textarea
          ref={useComposedRefs(ref, textareaRef)}
          id={textareaId}
          rows={3}
          onChange={(e) => {
            if (textareaContainerRef.current && autoResize) {
              textareaContainerRef.current.style.height = 'auto';
              textareaContainerRef.current.style.height = `${e.target.scrollHeight + space.s2}px`;
            }
            onChange?.(e);
          }}
          className={clsx(textarea, className)}
          {...rest}
        />
      </div>
      {errorMessage && <p className={inputError}>{errorMessage}</p>}
    </div>
  );
};
